import React from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import DnsOutlinedIcon from "@mui/icons-material/DnsOutlined";

export type MappingStatus = "mapped" | "unmapped" | "mapped_elsewhere";

export type MapConfirmDeviceRow = {
  scan_device_id: string;
  hostname: string;
  mapping_status: MappingStatus;
  mapped_template_id?: string | null;
  mapped_template_name?: string | null;
  /** Per-row target when mapping multiple vendor/OS groups at once */
  target_template_id?: string | null;
  target_template_name?: string | null;
};

type Props = {
  open: boolean;
  action: "map" | "unmap" | null;
  rows: MapConfirmDeviceRow[];
  templateName?: string;
  templateNamesById?: Record<string, string>;
  isDarkMode?: boolean;
  saving?: boolean;
  onClose: () => void;
  onConfirm: () => void;
};

const MapTemplateConfirmDialog: React.FC<Props> = ({
  open,
  action,
  rows,
  templateName,
  templateNamesById = {},
  isDarkMode = false,
  saving = false,
  onClose,
  onConfirm,
}) => {
  if (!action) return null;

  const isUnmap = action === "unmap";
  const uniqueTargetNames = Array.from(
    new Set(
      rows
        .map((r) => r.target_template_name || templateName || "")
        .filter(Boolean)
    )
  );
  const hasMultipleTargets = !isUnmap && uniqueTargetNames.length > 1;
  const defaultNewTemplate = isUnmap ? "None" : templateName || "template";
  const count = rows.length;
  const replaceRows = isUnmap
    ? rows
    : rows.filter((r) => r.mapping_status === "mapped_elsewhere");
  const newRows = isUnmap
    ? []
    : rows.filter((r) => r.mapping_status !== "mapped_elsewhere");
  const hasReplace = replaceRows.length > 0;
  const isRemapFlow = !isUnmap && hasReplace;

  const titleAsset =
    count === 1
      ? rows[0]?.hostname || rows[0]?.scan_device_id || "asset"
      : `${count} assets`;

  const title = isUnmap
    ? `Unmap ${titleAsset} from ${templateName || "template"}?`
    : hasMultipleTargets
      ? `${hasReplace ? "Remap" : "Map"} ${titleAsset}`
      : isRemapFlow || count > 1
        ? `${hasReplace ? "Remap" : "Map"} ${titleAsset} to ${defaultNewTemplate}`
        : `Map ${titleAsset} to ${defaultNewTemplate}?`;

  const subtitle = isUnmap
    ? "Review what will be unmapped before you confirm."
    : "Review what changes before you confirm.";

  const primaryCta = isUnmap
    ? `Unmap ${count} asset${count > 1 ? "s" : ""}`
    : hasReplace
      ? `Remap ${count} asset${count > 1 ? "s" : ""}`
      : `Map ${count} asset${count > 1 ? "s" : ""}`;

  const resolveCurrent = (row: MapConfirmDeviceRow) =>
    row.mapping_status === "unmapped"
      ? "None"
      : row.mapped_template_name ||
        (row.mapped_template_id
          ? templateNamesById[row.mapped_template_id] || row.mapped_template_id
          : "None");

  const renderAssetRow = (
    row: MapConfirmDeviceRow,
    opts?: { showHistory?: boolean; last?: boolean; variant?: "replace" | "new" | "unmap" }
  ) => {
    const assetName = row.hostname || row.scan_device_id || "-";
    const fromTpl = isUnmap ? templateName || "template" : resolveCurrent(row);
    const toTpl = isUnmap
      ? "None"
      : row.target_template_name ||
        (row.target_template_id
          ? templateNamesById[row.target_template_id] || row.target_template_id
          : defaultNewTemplate);
    const isNewMapping = opts?.variant === "new";

    return (
      <Box
        key={row.scan_device_id}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          py: 1.25,
          borderBottom: opts?.last
            ? "none"
            : isDarkMode
              ? "1px solid #2E323F"
              : "1px solid #EAECF0",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.25, minWidth: 0 }}>
          <DnsOutlinedIcon
            sx={{
              fontSize: 20,
              mt: "2px",
              color: isDarkMode ? "#9CA3AF" : "#667085",
              flexShrink: 0,
            }}
          />
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                fontFamily: "Manrope, sans-serif",
                fontSize: "14px",
                fontWeight: 700,
                color: isDarkMode ? "#F9FAFB" : "#101828",
                lineHeight: 1.3,
              }}
            >
              {assetName}
            </Typography>
            {opts?.showHistory && (
              <Typography
                sx={{
                  mt: 0.25,
                  fontFamily: "Manrope, sans-serif",
                  fontSize: "12px",
                  fontWeight: 500,
                  color: isDarkMode ? "#9CA3AF" : "#667085",
                }}
              >
                History is kept.
              </Typography>
            )}
          </Box>
        </Box>

        {isNewMapping ? (
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 1,
              flexShrink: 0,
            }}
          >
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                px: 1,
                py: 0.25,
                borderRadius: "999px",
                bgcolor: isDarkMode ? "rgba(34, 197, 94, 0.15)" : "#ECFDF3",
                color: "#067647",
                fontFamily: "Manrope, sans-serif",
                fontSize: "11px",
                fontWeight: 700,
                border: "1px solid rgba(34, 197, 94, 0.28)",
              }}
            >
              New
            </Box>
            <Typography
              sx={{
                fontFamily: "Manrope, sans-serif",
                fontSize: "13px",
                fontWeight: 700,
                color: "#3A2ADA",
              }}
            >
              {toTpl}
            </Typography>
          </Box>
        ) : (
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.75,
              flexShrink: 0,
              fontFamily: "Manrope, sans-serif",
              fontSize: "13px",
              fontWeight: 600,
              color: isDarkMode ? "#D1D5DB" : "#475467",
            }}
          >
            <Box component="span">{fromTpl}</Box>
            <ArrowForwardRoundedIcon
              sx={{ fontSize: 16, color: isDarkMode ? "#6B7280" : "#98A2B3" }}
            />
            <Box
              component="span"
              sx={{
                color: isUnmap
                  ? isDarkMode
                    ? "#FCA5A5"
                    : "#B42318"
                  : "#3A2ADA",
                fontWeight: 700,
              }}
            >
              {toTpl}
            </Box>
          </Box>
        )}
      </Box>
    );
  };

  const sectionHeader = (
    label: string,
    sectionCount: number,
    tone: "replace" | "new" | "unmap"
  ) => {
    const styles =
      tone === "replace"
        ? {
            bgcolor: isDarkMode ? "rgba(245, 158, 11, 0.15)" : "#FEF0C7",
            color: isDarkMode ? "#FDBA74" : "#93370D",
          }
        : tone === "unmap"
          ? {
              bgcolor: isDarkMode ? "rgba(248,113,113,0.12)" : "#FEE4E2",
              color: isDarkMode ? "#FCA5A5" : "#B42318",
            }
          : {
              bgcolor: isDarkMode ? "rgba(58, 42, 218, 0.18)" : "#EEF2FF",
              color: "#3A2ADA",
            };

    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          mb: 0.5,
        }}
      >
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            px: 1.25,
            py: 0.4,
            borderRadius: "999px",
            fontFamily: "Manrope, sans-serif",
            fontSize: "12px",
            fontWeight: 700,
            ...styles,
          }}
        >
          {label}
        </Box>
        <Typography
          sx={{
            fontFamily: "Manrope, sans-serif",
            fontSize: "13px",
            fontWeight: 500,
            color: isDarkMode ? "#D1D5DB" : "#344054",
          }}
        >
          {sectionCount} asset{sectionCount === 1 ? "" : "s"}
        </Typography>
      </Box>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={() => {
        if (saving) return;
        onClose();
      }}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "16px",
          bgcolor: isDarkMode ? "#1E1E1E" : "#FFFFFF",
          fontFamily: "Manrope, sans-serif",
          overflow: "hidden",
        },
      }}
    >
      <DialogTitle
        sx={{
          position: "relative",
          fontFamily: "Manrope, sans-serif",
          fontWeight: 800,
          fontSize: "18px",
          color: isDarkMode ? "#F9FAFB" : "#101828",
          pr: 6,
          pb: 0.25,
        }}
      >
        {title}
        <Typography
          sx={{
            mt: 0.75,
            fontFamily: "Manrope, sans-serif",
            fontSize: "13.5px",
            fontWeight: 500,
            color: isDarkMode ? "#9CA3AF" : "#667085",
          }}
        >
          {subtitle}
        </Typography>
        <IconButton
          aria-label="Close"
          disabled={saving}
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 12,
            top: 12,
            color: isDarkMode ? "#9CA3AF" : "#98A2B3",
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 4.5, pb: 1 }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.25 }}>
          {isUnmap && replaceRows.length > 0 && (
            <Box>
              {sectionHeader("Remove mapping", replaceRows.length, "unmap")}
              <Box sx={{ mt: 0.5 }}>
                {replaceRows.map((row, idx) =>
                  renderAssetRow(row, {
                    showHistory: true,
                    last: idx === replaceRows.length - 1,
                    variant: "unmap",
                  })
                )}
              </Box>
            </Box>
          )}

          {!isUnmap && replaceRows.length > 0 && (
            <Box>
              {sectionHeader("Replaces template", replaceRows.length, "replace")}
              <Box sx={{ mt: 0.5 }}>
                {replaceRows.map((row, idx) =>
                  renderAssetRow(row, {
                    showHistory: true,
                    last: idx === replaceRows.length - 1,
                    variant: "replace",
                  })
                )}
              </Box>
            </Box>
          )}

          {!isUnmap && newRows.length > 0 && (
            <Box>
              {sectionHeader("New mapping", newRows.length, "new")}
              <Box sx={{ mt: 0.5 }}>
                {newRows.map((row, idx) =>
                  renderAssetRow(row, {
                    last: idx === newRows.length - 1,
                    variant: "new",
                  })
                )}
              </Box>
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5, pt: 1.5, gap: 1 }}>
        <Button
          variant="outlined"
          onClick={onClose}
          disabled={saving}
          sx={{
            textTransform: "none",
            fontFamily: "Manrope, sans-serif",
            fontWeight: 600,
            borderRadius: "10px",
            borderColor: isDarkMode ? "#4B5563" : "#D0D5DD",
            color: isDarkMode ? "#E5E7EB" : "#344054",
            bgcolor: "transparent",
            "&.MuiButton-outlined:hover": {
              borderColor: isDarkMode ? "#6B7280" : "#98A2B3",
              backgroundColor: isDarkMode ? "rgba(255,255,255,0.04)" : "#F9FAFB",
              color: isDarkMode ? "#F3F4F6" : "#344054",
            },
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          color={isUnmap ? "error" : "primary"}
          disabled={saving || count === 0}
          onClick={onConfirm}
          sx={{
            textTransform: "none",
            fontFamily: "Manrope, sans-serif",
            fontWeight: 700,
            borderRadius: "10px",
            px: 2,
            boxShadow: "none",
            color: "#FFFFFF",
            ...(isUnmap
              ? {
                  bgcolor: "#D92D20",
                  "&:hover": { bgcolor: "#B42318", boxShadow: "none", color: "#FFFFFF" },
                  "&.Mui-disabled": {
                    bgcolor: "rgba(217, 45, 32, 0.4)",
                    color: "rgba(255,255,255,0.8)",
                  },
                }
              : {
                  bgcolor: "#3A2ADA",
                  "&:hover": { bgcolor: "#2E21B6", boxShadow: "none", color: "#FFFFFF" },
                  "&.Mui-disabled": {
                    bgcolor: "rgba(58, 42, 218, 0.4)",
                    color: "rgba(255,255,255,0.8)",
                  },
                }),
          }}
        >
          {saving ? (
            <CircularProgress size={18} sx={{ color: "#fff" }} />
          ) : (
            primaryCta
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MapTemplateConfirmDialog;
